import type { EntityOptions } from 'typeorm';
import { Entity as TypeOrmEntity } from 'typeorm';

export interface CustomEntityMetadata {
	label?: string;
	isDvpTable?: boolean;
	/**
	 * Whether changes to this entity should be logged to `_audit` by
	 * AuditLogSubscriber. Default = `true` (audit everything).
	 *
	 * Set to `false` for high-write or self-logging entities where
	 * audit rows would be noise:
	 *   - log-style tables (`access_logs`, `client_activity`)
	 *   - immutable telemetry (`sensor_reading`, `sensor_submission`)
	 *   - the audit infrastructure itself (`_audit`, `_request`)
	 *   - email send / event records (`email_record`, `email_event`)
	 *
	 * The flag is propagated to `_object._auditable` by the database
	 * sync process so legacy SM1 readers see the same gating. The
	 * runtime check happens in AuditLogSubscriber via ObjectLookupService.
	 */
	auditable?: boolean;
	/**
	 * Entity PROPERTY names whose values are secrets (OAuth access / refresh
	 * tokens, API secrets, bearer-link tokens). AuditLogSubscriber still
	 * audits the entity, and still writes these keys into `_audit._json` /
	 * `_previousValues` so the trail shows the field was set or changed, but
	 * replaces each non-null value with AUDIT_REDACTED_MARKER. `_audit` is
	 * readable in the Activity Logs screens, so a secret written there is
	 * exposed to every tenant admin (PD-16786).
	 *
	 * AuditLogSubscriber also redacts a short list of unambiguous secret
	 * property names on every entity as a safety net
	 * (AUDIT_ALWAYS_REDACTED_PROPERTIES in libs/core). Declare the list here
	 * anyway, so the intent is visible on the entity.
	 */
	auditRedactedProperties?: Array<string>;
}

const customEntityMetadataStorage = new Map<
	string,
	CustomEntityMetadata & { target: Function }
>();

export function Entity(
	options?: EntityOptions,
	customOptions?: CustomEntityMetadata,
): ClassDecorator;
export function Entity(
	name?: string,
	options?: EntityOptions,
	customOptions?: CustomEntityMetadata,
): ClassDecorator;
export function Entity(
	...params: Array<string | EntityOptions | CustomEntityMetadata | undefined>
): ClassDecorator {
	let customOptions: CustomEntityMetadata | undefined;

	if (
		params.length === 3 ||
		(params.length === 2 && typeof params[0] === 'object')
	) {
		customOptions = params.pop() as CustomEntityMetadata;
	}

	return (target: Function) => {
		customEntityMetadataStorage.set(target.name, {
			target,
			...customOptions,
		});

		TypeOrmEntity(...(params as [EntityOptions]))(target);
	};
}

// Helper to retrieve custom metadata
export function getCustomEntityMetadata(
	entityClass: Function,
): CustomEntityMetadata | undefined {
	return customEntityMetadataStorage.get(entityClass.name);
}

/**
 * Returns the registered `auditable` flag for an entity class. Returns
 * `undefined` when no decorator metadata exists OR the decorator
 * didn't specify the flag — callers must apply the "default auditable"
 * policy themselves so the default is centralized.
 */
export function getEntityAuditableFlag(
	entityClass: Function,
): boolean | undefined {
	return customEntityMetadataStorage.get(entityClass.name)?.auditable;
}

/**
 * Returns the entity's `auditRedactedProperties` (see CustomEntityMetadata),
 * or an empty list when it declares none.
 */
export function getEntityAuditRedactedProperties(
	entityClass: Function,
): ReadonlyArray<string> {
	return (
		customEntityMetadataStorage.get(entityClass.name)?.auditRedactedProperties ??
		[]
	);
}
